import { extendTheme } from "@chakra-ui/react";

export const colors = {
  theme: {
    lighterB2: "#B2FEFA",
    lighter18: "rgba(81, 196, 216, 0.18)",
    lighter: "#26D9F8",
    light: "#54F5FF",
    light5: "rgba(84, 245, 255, 0.05)",
    light10: "rgba(84, 245, 255, 0.1)",
    light20: "rgba(84, 245, 255, 0.2)",
    light30: "rgba(84, 245, 255, 0.3)",
    light40: "rgba(84, 245, 255, 0.4)",
    light50: "rgba(84, 245, 255, 0.5)",
    light15: "rgba(84, 245, 255, 0.15)",
    deem: "#08D2F7",
    dark: "#0ED2F7",
    deep: "#00b5d8",
    deeper: "#38888E",
    deep96: "#008496",
    deep8a: "#066C8A",
    deep76: "#1D4B76",
    dull: "#042025",
    duller: "#1E2728",
  },
  white: {
    white10: "rgba(255, 255, 255, 0.1)",
    white20: "rgba(255, 255, 255, 0.2)",
    white60: "rgba(255, 255, 255, 0.6)",
    white65: "rgba(255, 255, 255, 0.65)",
    white45: "rgba(255, 255, 255, 0.45)",
    white100: "#FFFFFF",
    whitefd: "#FDFDFD",
  },
  black: {
    black18: "rgba(0, 0, 0, 0.18)",
    black30: "rgba(0, 0, 0, 0.3)",
    black40: "rgba(0, 0, 0, 0.4)",
    black50: "rgba(0, 0, 0, 0.5)",
    black100: "#000000",
    pearl: "#1B1C1D",
    black111: "#111111",
    dark: "#02050B",
  },
  red: "#F04438",
  green: {
    light: "#32D583",
    dark: "#008000",
    blueGreen: "#307393",
    blueGreen95: "#308895",
  },
  orange: "#F5841F",
  brand: "#44E1F9",
  gray: {
    silver: "#D9D9D9",
    lighter: "#9898B3",
    light: "#98A2B3",
    lightSlate: "#536177",
    slate: "#667085",
    slate67: "#676D79",
    slate6a: "#6A6A6A",
    slate50: "rgba(1, 0, 36, 0.50)",
    dark: "#475467",
    darker: "#393D4E",
    gloomy: "#344054",
    gloomy50: "rgba(52, 64, 84, 0.5)",
    cyanBlue: "#354155",
    dull: "#2d2e3f",
    dull28: "#282828",
    dull20: "#202D4A",
    deep: "#181818",
    deep50: "rgba(24, 33, 52, 0.5)",
    darkSlate: "#151515",
    darkSlate1c: "#1C1B1F",
    darkSlate80: "rgba(22, 36, 50, 0.80)",
    deeper: "#101828",
    sombre: "#101010",
    sombre30: "rgba(16, 16, 16, 0.3)",
    sombre70: "rgba(16, 16, 16, 0.7)",
    sombre1A: "#1A1A1A",
    sombre0d: "#0D1927",
    sombre1f: "#1F1E25",
    sombre1b: "#1B1C1D",
    sombre1e: "#1E2728",
    sombre15: "#151616",
    desolate: "#1C232F",
    darkDesolate: "#191919",
  },
  purple: {
    lavender: "#EAE8FE",
    medium: "#8884d8",
    blue: "#5498FF",
    dark: "#797BAF",
  },
  yellow: {
    dark: "#C2BD60",
    lemon: "#D1CD48",
    light: "#FFF849",
    bright: "#FFD132",
  },
  pink: {
    pale: "#361738",
    normal: "#FF80E3",
    normal20: "rgba(223, 112, 199, 0.20)",
    dark: "#2E102D",
    dark40: "rgba(82, 11, 88, 0.40)",
    darker: "#321722",
  },
};

export const gradients = {
  theme2br: `linear(to-br, ${colors.theme.lighterB2}, ${colors.theme.dark})`,
  themeDark: `linear-gradient(134deg, ${colors.theme.lighterB2} 0%,${colors.theme.dark} 100%,${colors.theme.dark} 100%);`,
  darkBlue:
    "linear-gradient(180deg, rgba(0,65,125,0.3) 0%, rgba(47, 116, 181, 0.23) 100%)",
  greenBlue:
    "linear-gradient(153deg, rgba(48, 136, 149, 0.20) 0%, rgba(0, 0, 0, 0.20) 97.63%)",
  lightPink: "linear-gradient(94deg, #4E1248 0%, #2A0930 100%)",
  darkPink: "linear-gradient(94deg, #7C2574 0%, #491352 100%)",
  sliderBg: `linear-gradient(90deg, #0AA9C1 0%, ${colors.theme.light} 100%)`,
};

export const borders = {
  dimGray: `1px solid ${colors.gray.gloomy}`,
  themeLightThin: `1px solid ${colors.theme.light}`,
  themeLightThiner: `0.5px solid ${colors.theme.light}`,
  themeLight30Thiner: `0.5px solid ${colors.theme.light30}`,
  themeLight40Thiner: `0.5px solid ${colors.theme.light40}`,
  themeLightThick: `3px solid ${colors.theme.light}`,
  blueGreen95Thin: `1px solid ${colors.green.blueGreen95}`,
  themeLightThiner30: `0.5px solid rgba(84, 245, 255, 0.3)`,
  dashedGraySlate: `1px dashed ${colors.gray.slate}`,
  dasheThinSlate: `0.5px dashed ${colors.gray.slate}`,
  dasheThinWhite: `0.5px dashed ${colors.white.white100}`,
  dashedThemeLight: `1px dashed ${colors.theme.light}`,
  pinkThin: `1px solid ${colors.pink.normal}`,
  darkGreenThin: `1px solid #175D7B`,
  desolateThin: `1px solid ${colors.gray.desolate}`,
};

export const boxShadow = {
  black18: `0px 1.2px 2.4px ${colors.black.black18}`,
};

const customTheme = extendTheme({
  colors,
  components: {
    Button: {
      baseStyle: {
        _focus: {
          boxShadow: "none",
        },
      },
      sizes: {
        lg: {
          fontSize: "16px",
          px: "20px",
        },
        md: {
          fontSize: "14px",
          px: "16px",
        },
        sm: {
          fontSize: "12px",
          px: "12px",
        },
      },
    },
  },
});

export default customTheme;
